import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LanguageToggle } from '../context/LanguageContext';
import { isSuperAdmin } from '../data/superAdmins';
import { LogOut } from 'lucide-react';

export default function Navbar() {
  const { user, viewMode, canSwitchView, switchView, logout } = useAuth();
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(100);
  const onLogout = () => { logout(); navigate('/login'); };

  const changeZoom = (val) => {
    setZoom(val);
    document.getElementById('orbit-main')?.style.setProperty('zoom', val / 100);
  };

  return (
    <nav
      className="px-4 md:px-6 py-1.5 md:py-3 flex items-center justify-between shrink-0 sticky top-0 z-30 bg-white/85 backdrop-blur-xl"
      style={{ borderBottom: '1px solid var(--line)', boxShadow: 'var(--shadow-xs)' }}
    >
      <div className="flex items-center gap-2 md:gap-3">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{
            background: 'linear-gradient(180deg, var(--brand-600), var(--brand-800))',
            boxShadow: 'var(--shadow-sm), inset 0 1px 0 rgba(255,255,255,0.18)',
          }}
        >
          <span className="text-white font-bold text-[11px] tracking-tight">CI</span>
        </div>
        <span className="text-gray-900 font-semibold text-sm hidden sm:inline tracking-tight">Capital Infusion</span>
        <span className="hidden sm:inline w-px h-3.5" style={{ background: 'var(--line-strong)' }} />
        <span className="text-xs font-medium hidden sm:inline o-muted">
          {viewMode === 'client' ? 'Client Portal' : 'Operations'}
        </span>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        <LanguageToggle />
        <div className="hidden sm:flex items-center gap-1 rounded-lg px-1.5 py-0.5" style={{ background: 'var(--ink-100)' }}>
          <button onClick={() => changeZoom(Math.max(70, zoom - 10))} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-white text-sm font-bold rounded-md transition-all">−</button>
          <span className="text-xs w-9 text-center tabular font-medium o-muted">{zoom}%</span>
          <button onClick={() => changeZoom(Math.min(130, zoom + 10))} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-white text-sm font-bold rounded-md transition-all">+</button>
        </div>
        {canSwitchView && (
          <div className="hidden md:flex items-center rounded-lg p-0.5 gap-0.5" style={{ background: 'var(--ink-100)' }}>
            <button onClick={() => switchView('rep')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'rep' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Rep View</button>
            <button onClick={() => switchView('client')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'client' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Client View</button>
          </div>
        )}
        <div className="flex items-center gap-2.5 pl-2 md:pl-3" style={{ borderLeft: '1px solid var(--line)' }}>
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white"
            style={{
              background: 'linear-gradient(180deg, var(--brand-500), var(--brand-700))',
              boxShadow: 'var(--shadow-xs), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
          >
            {user?.name?.[0] || '?'}
          </div>
          <div className="hidden sm:block">
            <p className="text-xs font-semibold leading-tight" style={{ color: 'var(--ink-800)' }}>{user?.name}</p>
            {/* Display label only  the underlying role is unchanged. Super admin
                is shown so the distinction is visible to the people who hold it,
                rather than reading as a plain admin account. */}
            <p className="text-[11px] leading-tight capitalize o-muted">
              {user?.email === 'matthew@capital-infusion.com'
                ? 'CEO'
                : isSuperAdmin(user) ? 'Super Admin' : user?.role}
            </p>
          </div>
          <button onClick={onLogout} className="ml-1 p-1.5 max-md:-mr-2 max-md:min-w-[44px] max-md:min-h-[44px] max-md:flex max-md:items-center max-md:justify-center rounded-lg transition-all text-gray-400 hover:text-gray-800 hover:bg-gray-100" title="Sign out">
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </nav>
  );
}
